import { css } from '@emotion/css';
import { Layout } from '../../../shared/components/layout/Layout';
import { h1Style, p2Style } from '../../../shared/styles/text';
import { useUser } from '../../../shared/providers/user-provider';
import { TextInput } from '../../../shared/components/TextInput';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../../shared/components/Button';
import { ChevronLeft } from '../../../shared/assets/icons';
import { useNavigator } from '../../../routing/navigator';
import { useUpdateMyEmail } from '../../../shared/services/api/user';
import { UpdateMyEmailParams } from '../../../shared/types/input-param-types';
import { useState } from 'react';
import { Colors } from '../../../shared/styles/colors';
import { showPrivateToast } from '../../../shared/utils/show-private-toast';

const validationSchema = yup.object({
  email: yup.string().required(),
});

export const ChangeEmailPage = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigator = useNavigator();
  const user = useUser();
  const updateMyEmail = useUpdateMyEmail();
  const methods = useForm({
    defaultValues: {
      email: user?.email || '',
    } as UpdateMyEmailParams,
    resolver: yupResolver(validationSchema) as any,
    mode: 'all',
  });

  const handlePersonalInformationUpdate: SubmitHandler<
    UpdateMyEmailParams
  > = async (data: UpdateMyEmailParams) => {
    try {
      setIsLoading(true);
      await updateMyEmail(data);
      navigator.toVerifyEmailPage();
    } catch (err: any) {
      showPrivateToast('Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout.Content className={emailPageStyle}>
      <Button
        startIcon={<ChevronLeft />}
        variant={ButtonVariantEnum.TEXT}
        size={ButtonSizeEnum.LARGE}
        text={`account`}
        className={backButtonStyle}
        onClick={navigator.toPrevious}
      />
      <div className={emailPageHeaderStyle}>
        <h1>Change Email</h1>
        <span className={subtitleStyle}>
          After you enter your new email address, you will have to verify it.
        </span>
      </div>
      <FormProvider {...methods}>
        <form
          className={formStyle}
          onSubmit={methods.handleSubmit(handlePersonalInformationUpdate)}
        >
          <TextInput
            placeHolder={'New email'}
            name="email"
            type="email"
            markAsRequired={true}
          />
          <Button
            disabled={isLoading}
            variant={ButtonVariantEnum.FILLED}
            size={ButtonSizeEnum.LARGE}
            fullWidth={true}
            text="next"
            type={'submit'}
            loading={isLoading}
            className={submitButtonStyle}
          />
        </form>
      </FormProvider>
    </Layout.Content>
  );
};

const formStyle = css`
  padding-top: 2rem;
`;

const emailPageStyle = css`
  padding-top: 0.625rem;
`;

const backButtonStyle = css`
  padding-top: 0rem;
  margin-bottom: 2rem;
`;

const subtitleStyle = css`
  ${p2Style}
  color: ${Colors.TEXT_SECONDARY};
`;

const submitButtonStyle = css`
  margin-top: 1.5rem;
`;

const emailPageHeaderStyle = css`
  h1 {
    ${h1Style}
    margin-bottom: 0.75rem;
  }
`;
